import React, { createContext, useContext, useState, useEffect } from 'react';
import { initializeApp, FirebaseApp } from 'firebase/app';
import { 
  getAuth, 
  signInWithPhoneNumber, 
  RecaptchaVerifier,
  onAuthStateChanged,
  signOut as firebaseSignOut,
  User
} from 'firebase/auth';

// Your Firebase config
const firebaseConfig = {
  // Add your Firebase config here
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID
};

// Initialize Firebase
let app: FirebaseApp | undefined;
try {
  app = initializeApp(firebaseConfig);
} catch (error) {
  console.error('Error initializing Firebase:', error);
}

const auth = getAuth(app);

interface AuthContextType {
  currentUser: User | null;
  loading: boolean;
  token: string | null;
  signInWithPhone: (phoneNumber: string) => Promise<void>;
  verifyOTP: (otp: string) => Promise<void>;
  signOut: () => Promise<void>;
  refreshToken: () => Promise<string | null>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true); // Start with loading true
  const [token, setToken] = useState<string | null>(null);
  const [confirmationResult, setConfirmationResult] = useState<any>(null);
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    if (!app) {
      setLoading(false);
      setInitialized(true);
      return;
    }

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      try {
        setCurrentUser(user);
        if (user) {
          const token = await user.getIdToken();
          setToken(token);
          // Set up token refresh
          const refreshInterval = setInterval(async () => {
            const newToken = await refreshToken();
            if (newToken) setToken(newToken);
          }, 14 * 60 * 1000); // Refresh token every 14 minutes

          return () => clearInterval(refreshInterval);
        } else {
          setToken(null);
        }
      } catch (error) {
        console.error('Error in auth state change:', error);
      } finally {
        setLoading(false);
        setInitialized(true);
      }
    });

    return unsubscribe;
  }, []);

  const signInWithPhone = async (phoneNumber: string) => {
    if (!app) throw new Error('Firebase not initialized');
    setLoading(true);
    try {
      const recaptchaVerifier = new RecaptchaVerifier(auth, 'recaptcha-container', {
        size: 'invisible'
      });
      
      const confirmation = await signInWithPhoneNumber(auth, phoneNumber, recaptchaVerifier);
      setConfirmationResult(confirmation);
    } catch (error) {
      console.error('Error in signInWithPhone:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const verifyOTP = async (otp: string) => {
    if (!app) throw new Error('Firebase not initialized');
    setLoading(true);
    try {
      if (!confirmationResult) throw new Error('No confirmation result found');
      const result = await confirmationResult.confirm(otp);
      const token = await result.user.getIdToken();
      setToken(token);
      setCurrentUser(result.user);
    } catch (error) {
      console.error('Error in verifyOTP:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const signOut = async () => {
    if (!app) throw new Error('Firebase not initialized');
    setLoading(true);
    try {
      await firebaseSignOut(auth);
      setToken(null);
      setCurrentUser(null);
    } catch (error) {
      console.error('Error in signOut:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const refreshToken = async () => {
    if (!app) return null;
    try {
      const user = auth.currentUser;
      if (user) {
        const newToken = await user.getIdToken(true);
        setToken(newToken);
        return newToken;
      }
      return null;
    } catch (error) {
      console.error('Error refreshing token:', error);
      return null;
    }
  };

  const value = {
    currentUser,
    loading,
    token,
    signInWithPhone,
    verifyOTP,
    signOut,
    refreshToken
  };

  // Show loading state or error state if not initialized
  if (!initialized) {
    return <div className="flex items-center justify-center min-h-screen">Loading...</div>;
  }

  // If Firebase failed to initialize, show error state
  if (!app) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen text-red-500">
        <p>Failed to initialize Firebase.</p>
        <p>Please check your configuration.</p>
      </div>
    );
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}; 